import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Grid,
  Card,
  CardMedia,
  LinearProgress,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Lightbox from "yet-another-react-lightbox";
import { AttachmentApi } from "./api/attachmentApi";
import { SegmentImages } from "components/QueueBoard/models/workOrderTypes";
import {
  accordionStyle,
  accordionSummaryStyle,
  accordionTitleStyle,
  counterChipStyle,
} from "../workOrderSegmentDetailsDialogStyles";
import { useSnackbar } from "notistack";
import { getLocalTime } from "components/common/Utils/commonUtilityFunctions";
import { User } from "components/authentication/models/authTypes";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import packageJson from "../../../../../../package.json";
import localStorageHelper from "../../../../../app/localStorage";
interface SegmentAttachedImagesProps {
  images: SegmentImages[];
  segmentIdentity: number;
  isReadOnly?: boolean;
}

const SegmentAttachedImages: React.FC<SegmentAttachedImagesProps> = ({
  images,
  segmentIdentity,
}) => {
  const theme = useTheme();
  const [openImg, setOpenImg] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedCount, setUploadedCount] = useState<number>(0);
  const [uploadTotal, setUploadTotal] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [imagesCollection, setImagesCollection] = useState(images);

  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );

  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    const loadThumbnails = async () => {
      //console.log("fetch thumbnails");
      const promises = imagesCollection.map((imageObj) =>
        AttachmentApi.fetchThumbnail(imageObj.imageIdentity)
      );
      const urls = await Promise.all(promises);
      setThumbnailUrls(urls);
    };

    loadThumbnails();
  }, [imagesCollection]);
  // Inside your component
  const [slides, setSlides] = useState<{ src: string }[]>([]);

  // When a thumbnail is clicked
  const handleImageClick = async (index: number) => {
    try {
      const slide = await AttachmentApi.fetchSlideImage(
        imagesCollection[index].imageIdentity
      );
      setSlides([slide]);
      setOpenImg(true);
    } catch (err) {
      console.error("Error fetching image", err);
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "openImageInFullView",
      {
        elapsed_ms: "124",
        title: "Segment Images",
        componentName: "AttachFiles.tsx",
        version: packageJson.version,
      }
    );
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles = Array.from(files);
    const oversizedFile = validFiles.find(
      (file) => file.size > 10 * 1024 * 1024
    );
    if (oversizedFile) {
      enqueueSnackbar(`File "${oversizedFile.name}" exceeds the 10MB limit.`, {
        variant: "error",
      });
      return;
    }

    setUploadTotal(validFiles.length);
    setUploadedCount(0);
    setUploadProgress(0);

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("segmentIdentity", segmentIdentity.toString());
      formData.append("file", file);
      formData.append("DisplayText", file.name); // or any user-entered text
      formData.append("OrigFileName", file.name);
      formData.append("Extension", file.name.split(".").pop() || "");
      formData.append("StreamLength", file.size.toString());
      formData.append("MIME", file.type);
      formData.append("Source", "inv.Segment");
      formData.append("SourceId", segmentIdentity.toString());
      formData.append("IsActive", "true");
      formData.append("ModBy", user.userName);
      formData.append("EntBy", user.userName);
      formData.append("ModDate", getLocalTime());
      formData.append("EntDate", getLocalTime());
      try {
        const result = await AttachmentApi.uploadImage(
          formData,
          (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        );

        //const updatedImages = [...images, { imageIdentity: result.data.id }];
        setImagesCollection((prev) => [
          ...prev,
          {
            imageIdentity: result.data.id,
            segmentIdentity: segmentIdentity,
            attachmentIdentity: 0,
          },
        ]);
        // After each upload success
        setUploadedCount((prev) => prev + 1);

        // Refresh thumbnail
        // Ideally, get from server the new image ID or just refetch all thumbnails
        // Here for demo, we just refetch all thumbnails
        const urls = await Promise.all(
          imagesCollection.map((imageObj) =>
            AttachmentApi.fetchThumbnail(imageObj.imageIdentity)
          )
        );
        setThumbnailUrls(urls);
      } catch (error) {
        console.error("Upload failed", error);
        enqueueSnackbar(`Uploading failed, please retry again later.`, {
          variant: "error",
        });
      } finally {
        setUploadTotal(0);
      }
    }

    setSnackbarOpen(true);
  };

  const handleRemoveImage = async (imageIdentity: number) => {
    const updatedImages = imagesCollection.filter(
      (img) => img.imageIdentity !== imageIdentity
    );
    setImagesCollection(updatedImages);

    // If thumbnailUrls aligns 1:1 with imagesCollection:
    const updatedThumbnails = updatedImages.map((img) => {
      const imgIndex = imagesCollection.indexOf(img);
      return thumbnailUrls[imgIndex];
    });
    setThumbnailUrls(updatedThumbnails);

    try {
      const result = await AttachmentApi.removeAttachedImage(imageIdentity);
      enqueueSnackbar(`Image removed successfully`, {
        variant: "success",
      });
    } catch (err) {
      //console.error("Error fetching image", err);
      enqueueSnackbar(`Deletion failed, please retry again later.`, {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Accordion
        sx={{
          ...accordionStyle,
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#fff" : "#181F2A",
        }}
      >
        <AccordionSummary
          sx={accordionSummaryStyle}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography gutterBottom sx={accordionTitleStyle}>
            Images
            <Chip
              variant="outlined"
              label={imagesCollection.length}
              size="small"
              sx={counterChipStyle}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#f5f5f5" : "#232323",
          }}
        >
          {/* <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              component="label"
              size="small"
              sx={{ textTransform: "none" }}
            >
              Upload Image(s)
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
          </Box> */}

          {uploadTotal > 0 && (
            <div style={{ marginTop: 10 }}>
              <Typography>
                Uploading {uploadedCount} of {uploadTotal}
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </div>
          )}

          <Grid
            container
            spacing={1}
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#f5f5f5" : "#232323",
              p: "5px",
              mt: "5px",
            }}
          >
            {imagesCollection.map((imageObject, index) => (
              <Grid item xs={1} sm={1} lg={1} key={index}>
                <Card sx={{ position: "relative", cursor: "pointer" }}>
                  {/* Remove icon button */}
                  {/* <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                      width: "16px",
                      height: "16px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the image click
                      handleRemoveImage(imageObject.imageIdentity);
                    }}
                  >
                    <CloseIcon
                      sx={{ width: "16px", height: "16px" }}
                      fontSize="small"
                    />
                  </IconButton> */}

                  {/* Thumbnail image */}
                  <CardMedia
                    component="img"
                    height="56px"
                    width="28px"
                    image={thumbnailUrls[index]}
                    alt={`Image ${index}`}
                    onClick={() => handleImageClick(index)}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Lightbox
        open={openImg}
        close={() => {
          setOpenImg(false);
          setSlides([]);
        }}
        slides={slides}
        index={0}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
      />
    </>
  );
};

export default SegmentAttachedImages;
